import moment from "moment/moment";
import { encode } from "js-base64";
import { styled } from '@mui/material/styles';
import { LinearProgress } from '@mui/material';
import React, { useState, useMemo } from "react";
import { Divider, Skeleton } from "@mui/material";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "../../../assets/styles/containers/_icTable.scss";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SortIcon, PreviewIcon } from "../../../assets/svgIcons/Svg"
import { Menu, MenuItem, ListItemIcon, ListItemText, Table, Tooltip, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Typography, Box, Chip, Button, IconButton } from "@mui/material";
import { Checkbox } from "@mui/material";

const StyledProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 30,
    borderRadius: 6,
    backgroundColor: '#F1F3F4', // light background
    '& .MuiLinearProgress-bar': {
        backgroundColor: '#4282E2', // light blue bar
        borderRadius: 6,
        opacity: 0.14
    },
}));

const headers = [
    { key: "order", label: "Template name" },
    { key: "templates", label: "Description" },
    { key: "inspectors", label: "Published" },
    { key: "lastModified", label: "Last used" },
    { key: "active", label: "" },
];

const statusColors = {
    "completed": { primary: "#00644A", secondary: "#C7F6E0" },
    "scheduled": { primary: "#1F2F9C", secondary: "#C7DBF6" },
    "unscheduled": { primary: "#B71C1C", secondary: "#FFCDD2" },
    "NOT STARTED": { primary: "#1F2F9C", secondary: "#C7DBF6" },
    "IN PROGRESS": { primary: "#CB8530", secondary: "#FFF9C8" },
    "MARKED COMPLETE": { primary: "#00644A", secondary: "#C7F6E0" },
};

// const IR_URL = await IRURL();
// const RW_URL = await RWURL();
const associateCompany = sessionStorage.getItem("associate_company");

const ICTable = ({ data, sortDirection, setSortDirection, sortBy, setSortBy, setSelectedOrders, selectedOrders, setFromDate, selectedDate, setSelectedDate, changeStatus, clearAllFilters, setToDate, search, setPage, status, setStatus, setInputText, loading, page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange }) => {
    // const [sortBy, setSortBy] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    // const [sortDirection, setSortDirection] = useState("asc");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event, order) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrder(order);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedOrders([]);
    };

    const sortFieldMap = {
        order: 'location_address',
        templates: 'template_name',
        inspectors: 'inspector_name',
        lastModified: 'updated_at',
        status: 'status',
    };

    const handleSort = (key) => {
        const apiField = sortFieldMap[key] || 'updated_at';

        if (sortBy === apiField) {
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(apiField); // Use backend field name
            setSortDirection("asc");
        }
        setPage(0); // Reset pagination
    };


    const sortedOrders = useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            let valA, valB;

            const getEffectiveDate = (order) =>
                new Date(order.updatedAt || order.createdAt || "1900-01-01");

            // Default sorting: latest updatedAt or createdAt (DESC)
            if (!sortBy) {
                const dateA = getEffectiveDate(a);
                const dateB = getEffectiveDate(b);
                return dateB - dateA; // Newest first
            }

            // If sortBy is 'orderStartDate', use scheduled_dte
            if (sortBy === 'orderStartDate') {
                const dateA = new Date(a.scheduled_dte || "1900-01-01");
                const dateB = new Date(b.scheduled_dte || "1900-01-01");
                const dateCompare = dateB - dateA;
                return sortDirection === "asc" ? -dateCompare : dateCompare;
            }

            // Sort by selected field
            switch (sortBy) {
                case 'order':
                    valA = a.address1?.toLowerCase() || '';
                    valB = b.address1?.toLowerCase() || '';
                    break;
                case 'templates':
                    valA = a.inspection_templates?.map(t => t.name).join(", ").toLowerCase() || '';
                    valB = b.inspection_templates?.map(t => t.name).join(", ").toLowerCase() || '';
                    break;
                case 'inspectors':
                    valA = a.inspectors?.map(i => i.name).join(", ").toLowerCase() || '';
                    valB = b.inspectors?.map(i => i.name).join(", ").toLowerCase() || '';
                    break;
                case 'lastModified':
                    valA = new Date(a.updatedAt || a.createdAt || "1900-01-01");
                    valB = new Date(b.updatedAt || b.createdAt || "1900-01-01");
                    break;
                case 'status':
                    valA = a.status?.toLowerCase() || '';
                    valB = b.status?.toLowerCase() || '';
                    break;
                default:
                    // Fallback: updatedAt or createdAt
                    const fallbackDateA = getEffectiveDate(a);
                    const fallbackDateB = getEffectiveDate(b);
                    return fallbackDateB - fallbackDateA;
            }

            // Compare values for string/date fields
            if (valA < valB) return sortDirection === "asc" ? -1 : 1;
            if (valA > valB) return sortDirection === "asc" ? 1 : -1;

            // If equal, fallback to latest updatedAt/createdAt
            const fallbackDateA = getEffectiveDate(a);
            const fallbackDateB = getEffectiveDate(b);
            return fallbackDateB - fallbackDateA;
        });
    }, [data, sortBy, sortDirection]);


    // const formatDate = (dateStr) => {
    //     if (!dateStr) return "--";
    //     const date = new Date(dateStr);
    //     return isNaN(date.getTime())
    //         ? "--"
    //         : date.toLocaleDateString(undefined, {
    //             year: "numeric",
    //             month: "long",
    //             day: "numeric",
    //         });
    // };



    const allIds = useMemo(() => data?.map(r => r.id) ?? [], [data]);

    const handleSelectOne = (order) => (e) => {
        setSelectedOrders(prev =>
            e.target.checked
                ? [...prev, order]
                : prev.filter(o => o.id !== order.id)
        );
    };

    const handleSelectAll = (e) => {
        setSelectedOrders(e.target.checked ? [...data] : []);
    };

    // const isSelected = (id) => selectedOrders.some(order => order.id === id);



    const formatAddress = (order) => {
        const cityStateZip = [
            order.city,
            order.state,
            order.zip
        ].filter(Boolean).join(' '); // joins only non-null/defined values with spaces

        const parts = [
            order.address1,
            order.address2,
            cityStateZip
        ].filter(Boolean); // removes any null, undefined, or ""

        return parts.join(', '); // joins remaining parts with commas
    };

    const mockTemplates = [
        {
            id: 1,
            name: "House",
            isDefault: true,
            description: "Everything you need to do a typical home inspection.",
            publishedAt: "2023-01-10T08:15:00Z",
            lastUsedAt: "2023-01-10T08:15:00Z",
            reports: 17,
            status: "ACTIVE",
        },
        // {
        //     id: 2,
        //     name: "Commercial inspection",
        //     description:
        //         "Very description, lots and lots on info goes here and can be made to fit into two lines if needed",
        //     publishedAt: "2023-01-10T08:15:00Z",
        //     lastUsedAt: "2023-01-10T08:15:00Z",
        //     reports: 50,
        //     status: "ACTIVE",
        // },
    ];

    // helpers
    const isSelected = (id) => selectedOrders.some(o => o.id === id);

    const toggleOne = (order) => {
        setSelectedOrders(prev => {
            const exists = prev.some(o => o.id === order.id);
            return exists ? prev.filter(o => o.id !== order.id) : [...prev, order];
        });
    };

    const toggleAll = (checked) => {
        setSelectedOrders(checked ? [...(data || [])] : []);
    };


    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.08)',
                    },
                }}
            >
                {/* will require later */}
                <MenuItem onClick={() => { handleClose(); window.open("https://4isn.com" + selectedOrder?.order_url, '_blank'); }}>
                    <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{
                        className: 'vert-icon-text',
                    }}>View inspection details</ListItemText>
                </MenuItem>
                {selectedOrder?.status !== "NOT STARTED" && <MenuItem className="vert-icon-text" onClick={() => {
                    window.open(`?guid=${selectedOrder?.orderGuid}`, '_blank');
                    handleClose();
                }}>
                    <ListItemIcon >
                        <PreviewIcon />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{
                        className: 'vert-icon-text',
                    }}>Preview report</ListItemText>
                </MenuItem>}
            </Menu>
            {/* <div style={{ height: "57px", padding: "8px", backgroundColor: "red" }}>
            </div> */}
            <div style={{ height: "57px", display: "flex", padding: "8px", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                    {/* will require later */}
                    {/* <DateFilter selectedDate={selectedDate} setSelectedDate={setSelectedDate} setFromDate={setFromDate} setToDate={setToDate} setPage={setPage} /> */}
                    {/* <StatusFilter status={status} setStatus={setStatus} setPage={setPage} /> */}
                </div>
            </div>
            <Divider />
            <TableContainer component={Paper} elevation={0} sx={{ borderBottomLeftRadius: 8, borderBottomRightRadius: 8, maxHeight: "45vh" }}>
                {!loading && data?.length === 0 ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                        }}
                    >
                        <Typography sx={{ fontStyle: "italic", color: "#666", mb: 1 }}>
                            No matching reports found.
                        </Typography>
                        <Typography sx={{ color: "#999", mb: 2 }}>
                            Try adjusting your filters or search terms.
                        </Typography>
                        <Button
                            onClick={clearAllFilters}
                            variant="text"
                            sx={{
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                color: "#3b3b3b",
                                '&:hover': {
                                    textDecoration: 'underline',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            Clear all filters
                        </Button>
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ borderBottom: "2px solid var(--color-neutral-neutral-300, #D5D5D5)" }}>
                                {/* NEW checkbox column */}
                                <TableCell padding="checkbox" sx={{ backgroundColor: '#fff', zIndex: 3, width: 48 }}>
                                    <Checkbox
                                        disableRipple
                                        sx={{ mt: 0 }}
                                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < (data?.length || 0)}
                                        checked={(data?.length || 0) > 0 && selectedOrders.length === (data?.length || 0)}
                                        onChange={(e) => toggleAll(e.target.checked)}
                                        inputProps={{ 'aria-label': 'select all' }}
                                    />
                                </TableCell>

                                {headers?.map((header) => (
                                    <TableCell
                                        className="RSP_table_header"
                                        key={header.key}
                                        onClick={() => handleSort(header.key)}
                                        sx={{ cursor: "pointer", userSelect: "none", verticalAlign: 'top', backgroundColor: '#fff', zIndex: 3 }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {header.label}
                                            {header.label && <SortIcon />}
                                        </Box>
                                    </TableCell>
                                ))}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        {!loading && data?.length === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '60vh', // 🔥 Ensures vertical centering
                                    textAlign: 'center',
                                    width: '100%',
                                    fontSize: "16px",
                                    fontStyle: 'italic',
                                    fontFamily: "Source sans pro"
                                }}
                            >
                                <Box sx={{ fontStyle: 'italic', color: '#666', mb: 0.5 }}>
                                    No matching reports found.
                                </Box>
                                <Box sx={{ fontStyle: 'italic', color: '#999', mb: 2 }}>
                                    Try adjusting your filters or search terms.
                                </Box>
                                <Button
                                    onClick={clearAllFilters}
                                    variant="text"
                                    sx={{
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        color: '#3b3b3b',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    Clear all filters
                                </Button>
                            </Box>
                        )}
                        <TableBody>
                            {loading ? (
                                [...Array(8)].map((_, i) => (
                                    <TableRow key={`skeleton-${i}`}>

                                        <TableCell sx={{ padding: "16px 24px", width: "420px", minWidth: "370px" }}>
                                            <Box display="flex">
                                                <Skeleton variant="rounded" width={60} height={60} sx={{ mr: 2 }} />
                                                <Box flex={1}>
                                                    <Skeleton width="50%" height={24} />
                                                    <Skeleton width="80%" height={20} />
                                                    <Skeleton width="30%" height={18} />
                                                    <Box mt={1}>
                                                        <Skeleton width="100%" height={10} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "250px" }}>
                                            <Skeleton height={20} width="60%" />
                                            <Skeleton height={20} width="50%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "203px" }}>
                                            <Skeleton height={20} width="40%" />
                                            <Skeleton height={20} width="60%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "160px" }}>
                                            <Skeleton height={20} width="80%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 30px" }}>
                                            <Skeleton variant="rounded" width={80} height={24} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: 'top', pt: "20px" }}>
                                            <Skeleton variant="rectangular" width={120} height={32} />
                                        </TableCell>
                                    </TableRow>
                                ))) : data?.map((order, index) => {
                                    return (
                                        <React.Fragment key={`${order.id}-${index}`}>
                                            <TableRow>
                                                <TableCell
                                                    padding="checkbox"
                                                    onClick={() => toggleOne(order)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <Checkbox
                                                        disableRipple
                                                        checked={isSelected(order.id)}
                                                        tabIndex={-1}
                                                        // Make clicks go to the cell, not the checkbox (prevents double toggles)
                                                        sx={{ pointerEvents: 'none' }}
                                                        inputProps={{ 'aria-label': `select order ${order.id}` }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: "top", padding: "20px 24px", width: "250px" }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: "Open Sans",
                                                            color: 'text.primary',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: "13px",
                                                            overflow: 'hidden',
                                                            fontWeight: 400,
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '200px',
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {order?.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', padding: "20px 24px", width: "500px", cursor: 'default' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: "Open Sans",
                                                            color: 'text.primary',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '200px',
                                                            fontWeight: 400,
                                                            fontSize: "13px",
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {order.description}
                                                    </Typography>
                                                </TableCell>
                                                {/* <TableCell sx={{ verticalAlign: 'top', p: "8px 0px 0px 40px", width: "203px" }}>
                                                    {order.inspectors?.map((inspector, i) => (
                                                        <Typography key={i} variant="body2">{inspector.name}</Typography>
                                                    ))}
                                                </TableCell> */}
                                                <TableCell sx={{ verticalAlign: 'top', padding: "10px 24px", width: "160px" }}>
                                                    <Typography variant="body2" sx={{ fontFamily: "Open Sans", fontSize: "13px", }}>
                                                        {order.published_at ? moment(moment.utc(order.published_at).local()).format('L') : moment(order.published_at).format('L')}
                                                        <br />
                                                    </Typography>
                                                    <p style={{ fontFamily: '"Source Sans Pro", sans-serif', fontSize: "12px", color: "#757575" }}>{order.published_at ? moment(moment.utc(order.published_at).local()).format('LT') : moment(order.published_at).format('LT')}</p>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', padding: "10px 24px", width: "250px" }}>
                                                    <Typography variant="body2" sx={{ fontFamily: "Open Sans", fontSize: "13px", }}>
                                                        {order.last_used ? moment(moment.utc(order.last_used).local()).format('L') : "--"}
                                                        <br />
                                                    </Typography>
                                                    <p style={{ fontFamily: '"Source Sans Pro", sans-serif', fontSize: "12px", color: "#757575" }}> {order.last_used ? moment(moment.utc(order.last_used).local()).format('LT') : ""}</p>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', p: "10px 0px 0px 30px", width: "150px" }}>
                                                    {order?.is_active === 1 && <Chip
                                                        label="Active"
                                                        size="small"
                                                        className="status_chip"
                                                        sx={{ color: statusColors[order.status]?.primary, backgroundColor: statusColors[order.status]?.secondary }}
                                                    />}

                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                        </TableBody>
                        <div style={{ height: "13px" }}></div>
                    </Table>
                )}
                {/* <CustomTablePagination count={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange} /> */}
            </TableContainer>
        </>
    );
};

export default ICTable;